import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { X, Check, Loader2, Link2, Plus, Trash2, AlertCircle } from "lucide-react";

const validateUrl = (url, type) => {
  url = (url || "").trim();
  if (!url) return { valid: false, error: "" };
  if (!url.startsWith("http")) url = "https://" + url;
  url = url.replace("old.reddit.com", "www.reddit.com").replace("new.reddit.com", "www.reddit.com");

  // Strip tracking params (utm_*, share_*, context, rdt, ref, ref_source) for display + validation.
  // Server also strips these on ingest, but doing it here gives immediate visual feedback.
  try {
    const u = new URL(url);
    const trackingKeys = /^(utm_|share_|context$|rdt$|ref$|ref_source$|correlation_id$|chainedPosts$)/i;
    const params = new URLSearchParams();
    u.searchParams.forEach((v, k) => { if (!trackingKeys.test(k)) params.append(k, v); });
    u.search = params.toString();
    u.hash = "";
    url = u.toString().replace(/\/$/, "");
  } catch {}

  // Short share link: /r/{sub}/s/{token}  — the server resolves it via HTTP redirect on save.
  const shareMatch = url.match(/reddit\.com\/r\/([^/]+)\/s\/[A-Za-z0-9]+\/?$/i);
  if (shareMatch) {
    return { valid: true, url, subreddit: shareMatch[1], isShare: true };
  }

  const commentMatch = url.match(/reddit\.com\/r\/([^/]+)\/comments\/([a-z0-9]+)\/[^/]*\/([a-z0-9]+)\/?$/i);
  const commentMatchAlt = url.match(/reddit\.com\/r\/([^/]+)\/comments\/([a-z0-9]+)\/comment\/([a-z0-9]+)\/?$/i);
  const postMatch = url.match(/reddit\.com\/r\/([^/]+)\/comments\/([a-z0-9]+)\/?([^/]*)?\/?$/i);

  if (type === "Comment") {
    if (commentMatch || commentMatchAlt) return { valid: true, url, subreddit: (commentMatch || commentMatchAlt)[1] };
    if (postMatch) return { valid: false, error: "This is a post URL. Comment URLs must include the comment ID at the end." };
    return { valid: false, error: "Not a valid Reddit comment URL" };
  }
  if (type === "Post") {
    if (commentMatch || commentMatchAlt) return { valid: false, error: "This is a comment URL. Use type 'Comment' instead." };
    if (postMatch) return { valid: true, url, subreddit: postMatch[1] };
    return { valid: false, error: "Not a valid Reddit post URL" };
  }
  return { valid: false, error: "Select a type" };
};

const EntryRow = ({ row, index, onChange, onRemove, showRemove }) => {
  const result = row.url ? validateUrl(row.url, row.type) : null;
  const hasError = result && !result.valid && result.error;
  const isValid = result && result.valid;

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
      <div style={{ flex: 1 }}>
        <input
          value={row.url}
          onChange={(e) => onChange(index, "url", e.target.value)}
          className="filter-input"
          placeholder="Reddit URL — full, share (/s/…), or tracking link all work"
          style={{ width: "100%", borderColor: hasError ? "#ef4444" : isValid ? "#10b981" : undefined }}
          data-testid={"entry-url-" + index}
        />
        {hasError && (
          <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}>
            <AlertCircle size={11} /> {result.error}
          </div>
        )}
        {isValid && (
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
            r/{result.subreddit}
            {result.isShare && (
              <span style={{ marginLeft: 6, padding: "1px 6px", background: "#1e293b", color: "#60a5fa", borderRadius: 3, fontSize: 10 }}>
                share link — auto-resolved on save
              </span>
            )}
          </div>
        )}
      </div>
      <select
        value={row.type}
        onChange={(e) => onChange(index, "type", e.target.value)}
        className="filter-select"
        style={{ width: 110, flexShrink: 0 }}
        data-testid={"entry-type-" + index}
      >
        <option value="Post">Post</option>
        <option value="Comment">Comment</option>
      </select>
      {showRemove && (
        <button type="button" onClick={() => onRemove(index)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: "8px 4px", flexShrink: 0 }}>
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

export default function EntryForm({ entry, projects, onClose, onSaved }) {
  const { api, profile } = useAuth();
  const isEdit = !!entry;

  const userProjects = projects.filter((p) => {
    if (profile && profile.role === "admin") return true;
    if (profile && profile.project_ids) return profile.project_ids.includes(p.id);
    return true;
  });
  const defaultProject = userProjects.length > 0 ? userProjects[0].id : "";

  const [rows, setRows] = useState(
    isEdit ? [{ url: entry.url || "", type: entry.entry_type || "Post" }] : [{ url: "", type: "Post" }]
  );
  const [projectId, setProjectId] = useState(entry ? (entry.project_id || defaultProject) : defaultProject);
  const [status, setStatus] = useState(entry ? (entry.status || "unknown") : "unknown");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successCount, setSuccessCount] = useState(0);

  const handleRowChange = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { url: "", type: "Post" }]);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const allValid = rows.every((r) => {
    if (!r.url.trim()) return false;
    const result = validateUrl(r.url, r.type);
    return result.valid;
  }) && rows.length > 0 && projectId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setLoading(true);
    setError("");
    let added = 0;
    const errors = [];

    for (let i = 0; i < rows.length; i++) {
      const result = validateUrl(rows[i].url, rows[i].type);
      if (!result.valid) continue;
      try {
        if (isEdit && i === 0) {
          await api.patch("/entries/" + entry.id, {
            url: result.url, entry_type: rows[i].type, status: status,
            project_id: projectId, subreddit: result.subreddit || "",
          });
        } else {
          await api.post("/entries", {
            url: result.url, entry_type: rows[i].type, subreddit: result.subreddit,
            account: "", status: "unknown", project_id: projectId,
            marketer_name: profile ? profile.name : "",
            date: new Date().toISOString().split("T")[0],
          });
        }
        added++;
      } catch (err) {
        const detail = err.response ? (err.response.data ? err.response.data.detail : "Failed") : "Failed";
        errors.push(rows[i].url.substring(0, 40) + "...: " + detail);
      }
    }

    setSuccessCount(added);
    if (errors.length) {
      setError("Added " + added + "/" + rows.length + ". Errors: " + errors.join("; "));
    }
    if (added > 0) {
      onSaved();
      if (!errors.length) onClose();
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="entry-form-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{isEdit ? "Edit Entry" : "Add Entries"}</h3>
          <button className="btn btn-sm btn-secondary" onClick={onClose}><X size={16} /></button>
        </div>

        {error && <div className="login-error" style={{ whiteSpace: "pre-wrap" }}>{error}</div>}
        {successCount > 0 && !error && <div style={{ padding: "8px 16px", color: "#10b981", fontSize: 13 }}>Added {successCount} entries</div>}

        <form onSubmit={handleSubmit} className="modal-form" data-testid="entry-form">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: "#9ca3af" }}><Link2 size={12} /> URLs & Type</label>
            {!isEdit && (
              <button type="button" onClick={addRow} className="btn btn-sm btn-secondary" data-testid="add-row-btn"
                style={{ padding: "3px 10px", fontSize: 12 }}>
                <Plus size={12} /> Add More
              </button>
            )}
          </div>

          {rows.map((row, i) => (
            <EntryRow key={i} row={row} index={i} onChange={handleRowChange}
              onRemove={removeRow} showRemove={!isEdit && rows.length > 1} />
          ))}

          <div className="form-field full-width" style={{ marginTop: 12 }}>
            <label>Project</label>
            <select className="filter-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="entry-project">
              {userProjects.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </div>

          {isEdit && (
            <div className="form-field full-width" style={{ marginTop: 12 }}>
              <label>Status</label>
              <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="unknown">Unknown</option><option value="imported">To Be Assigned</option>
                <option value="assigned">Assigned</option><option value="live">Live</option><option value="deleted">Deleted</option>
              </select>
            </div>
          )}

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="submit" className="btn btn-primary" disabled={loading || !allValid} data-testid="entry-submit">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {isEdit ? " Save" : rows.length > 1 ? " Add " + rows.length + " Entries" : " Add Entry"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
